import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { api } from '../../services/api';
import usePagination from '../../hooks/usePagination';
import AccountsTable from './AccountsTable';
import ReportsTable from './ReportsTable';
import ReportDetailModal from './ReportDetailModal';

function StaffPage() {
  const { t } = useTranslation('admin');
  const { accounts } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();

  const [openReports, setOpenReports] = useState([]);
  const [resolvedReports, setResolvedReports] = useState([]);
  const [reportsStatus, setReportsStatus] = useState('loading');
  const [selectedReport, setSelectedReport] = useState(null);

  const userAccounts = useMemo(() => accounts.filter((item) => item.role === 'user'), [accounts]);
  const userPagination = usePagination(userAccounts, 10);
  const openReportsPagination = usePagination(openReports, 10);
  const resolvedReportsPagination = usePagination(resolvedReports, 10);

  const loadReports = useCallback(async () => {
    setReportsStatus('loading');
    try {
      const [openResult, resolvedResult] = await Promise.all([
        api.get('/reports?status=open'),
        api.get('/reports?status=resolved'),
      ]);
      setOpenReports(openResult.reports);
      setResolvedReports(resolvedResult.reports);
      setReportsStatus('ready');
    } catch (error) {
      setReportsStatus('error');
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const openReportUserIds = useMemo(() => new Set(openReports.map((report) => report.userId)), [openReports]);

  const handleToggleResolve = async (report, nextStatus) => {
    try {
      const { report: updated } = await api.patch(`/reports/${report.id}`, { status: nextStatus });
      if (updated.status === 'resolved') {
        setOpenReports((current) => current.filter((item) => item.id !== updated.id));
        setResolvedReports((current) => [updated, ...current.filter((item) => item.id !== updated.id)]);
      } else {
        setResolvedReports((current) => current.filter((item) => item.id !== updated.id));
        setOpenReports((current) => [updated, ...current.filter((item) => item.id !== updated.id)]);
      }
      setSelectedReport((current) => (current && current.id === updated.id ? updated : current));
      addToast(nextStatus === 'resolved' ? t('staff.reportResolved') : t('staff.reportReopened'), 'success');
    } catch (error) {
      addToast(error.message, 'error');
    }
  };

  return (
    <section className="page-card">
      <div className="page-header">
        <div>
          <p className="eyebrow">{t('staff.eyebrow')}</p>
          <h2>{t('staff.heading')}</h2>
        </div>
      </div>

      <div className="summary-grid">
        <article className="summary-card stat-card tint-lavender">
          <div className="stat-card-top">
            <span className="stat-icon" aria-hidden="true">👥</span>
            <span className="stat-label">{t('staff.totalUsers')}</span>
          </div>
          <strong>{userAccounts.length}</strong>
        </article>
        <article className="summary-card stat-card tint-pink">
          <div className="stat-card-top">
            <span className="stat-icon" aria-hidden="true">🚩</span>
            <span className="stat-label">{t('staff.unresolvedReports')}</span>
          </div>
          <strong>{openReports.length}</strong>
        </article>
      </div>

      <div className="sheet-tables-grid">
        <AccountsTable
          title={t('staff.userAccounts')}
          tint="lavender"
          accounts={userPagination.pageItems}
          showSubscription
          showOpenReports
          openReportUserIds={openReportUserIds}
          onRowClick={(account) => navigate(`/staff/users/${account.id}`)}
          emptyMessage={t('staff.noUserAccounts')}
          pagination={{ page: userPagination.page, totalPages: userPagination.totalPages, onChange: userPagination.setPage }}
        />

        <ReportsTable
          title={t('staff.unresolvedReportsTitle')}
          tint="rose"
          reports={openReportsPagination.pageItems}
          showMessage={false}
          showResolveAction
          onToggleResolve={handleToggleResolve}
          onRowClick={setSelectedReport}
          emptyMessage={reportsStatus === 'loading' ? t('staff.loadingReports') : t('staff.noUnresolvedReports')}
          pagination={{ page: openReportsPagination.page, totalPages: openReportsPagination.totalPages, onChange: openReportsPagination.setPage }}
        />

        <ReportsTable
          title={t('staff.resolvedReportsTitle')}
          tint="mint"
          reports={resolvedReportsPagination.pageItems}
          showMessage={false}
          showResolveAction
          onToggleResolve={handleToggleResolve}
          onRowClick={setSelectedReport}
          emptyMessage={reportsStatus === 'loading' ? t('staff.loadingReports') : t('staff.noResolvedReports')}
          pagination={{ page: resolvedReportsPagination.page, totalPages: resolvedReportsPagination.totalPages, onChange: resolvedReportsPagination.setPage }}
          fullWidth
        />
      </div>

      <ReportDetailModal report={selectedReport} onClose={() => setSelectedReport(null)} onToggleResolve={handleToggleResolve} />
    </section>
  );
}

export default StaffPage;
